import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { fetchAuthSession } from 'aws-amplify/auth'
import { config } from '../config'

const createApolloClient = () => {
  const getToken = async () => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString()
    } catch {
      return undefined
    }
  }

  /**
   * CACHE
   */
  const cache = new InMemoryCache()

  /**
   * LINK
   */
  const authLink = new SetContextLink(async prevContext => ({
    headers: { ...prevContext.headers, authorization: await getToken() },
  }))
  // Not BatchHttpLink — batching caused backend CPU spikes (fast ops blocked on slow ones)
  const httpLink = new HttpLink({ uri: config.BaseURL })
  const errorLoggerLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const graphQLError of error.errors) {
        console.error(graphQLError.message)
      }
    } else if (error) {
      console.error(error.message)
    }
  })

  const link = ApolloLink.from([authLink, errorLoggerLink.concat(httpLink)])

  /**
   * APOLLO CLIENT
   */
  const client = new ApolloClient({
    link,
    cache,
  })

  return {
    client,
  }
}

let apolloClient: ApolloClient

/** Use this function **once** to instantiate the apollo client, at the app top level */
export const initApolloClient = (): ReturnType<typeof createApolloClient> => {
  if (apolloClient) {
    console.error(
      '(re)instantiating apollo client in init function! Client should be instantiated exactly once!'
    )
  }

  const apollo = createApolloClient()
  apolloClient = apollo.client

  return apollo
}

export const getApolloClient = (): ApolloClient => {
  // fallback for runtime typesafety
  if (!apolloClient) {
    console.error(
      '(re)instantiating apollo client in getter! Client should have already been instantiated top level!'
    )
    return initApolloClient().client
  }
  return apolloClient
}
